"use client";
import { Badge, type BadgeProps } from "../Badge";
import { Skeleton } from "../Skeleton";
import { Tooltip } from "../Tooltip";

export type EnrollmentStatus = "enrolled" | "eligible" | "not_eligible" | "pending" | "opted_out";

export interface EnrollmentStatusBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** Enrollment status */
  status: EnrollmentStatus;
  /** Program name (shown in tooltip) */
  programName?: string;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * EnrollmentStatusBadge
 *
 * Displays device enrollment status for programs.
 * Color-coded status with optional program name in tooltip.
 *
 * Status mapping:
 * - enrolled → success (green)
 * - eligible → info (blue)
 * - pending → warning (yellow)
 * - not_eligible → neutral (gray)
 * - opted_out → neutral (gray)
 *
 * @example
 * ```tsx
 * <EnrollmentStatusBadge status="enrolled" programName="Demand Response Q4" />
 * <EnrollmentStatusBadge status="eligible" />
 * <EnrollmentStatusBadge status="pending" />
 * ```
 */
export function EnrollmentStatusBadge({
  status,
  programName,
  isLoading,
  size = "sm",
  shape = "pill",
  ...badgeProps
}: EnrollmentStatusBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-20" />;
  }

  const variantMap: Record<EnrollmentStatus, BadgeProps["variant"]> = {
    enrolled: "success",
    eligible: "info",
    pending: "warning",
    not_eligible: "neutral",
    opted_out: "neutral",
  };

  const labelMap: Record<EnrollmentStatus, string> = {
    enrolled: "Enrolled",
    eligible: "Eligible",
    pending: "Pending",
    not_eligible: "Not Eligible",
    opted_out: "Opted Out",
  };

  const badge = (
    <Badge variant={variantMap[status]} size={size} shape={shape} {...badgeProps}>
      {labelMap[status]}
    </Badge>
  );

  if (programName) {
    return <Tooltip content={programName}>{badge}</Tooltip>;
  }

  return badge;
}
